import { type VisibilityType } from '#types/System.types';

const isValidVisibilityType = (opt: unknown): opt is VisibilityType => {
  if (!opt || typeof opt !== 'object') return false;
  const o = opt as VisibilityType;
  return typeof o.id === 'string' && o.id.length > 0 && typeof o.shortLabel === 'string' && o.shortLabel.length > 0;
};

/**
 * Ensures system-required visibility types exist, while preserving any org customizations.
 * Ordering rules:
 * - 'internal' is always first
 * - 'pdfview' is always second
 * - all other items keep their relative order
 */
export const ensureDefaultVisibilityTypes = (
  raw: VisibilityType[] | undefined,
  defaults: VisibilityType[],
): VisibilityType[] => {
  const rawSafe = (raw ?? []).filter(isValidVisibilityType);

  const rawById = new Map<string, VisibilityType>();
  for (const opt of rawSafe) {
    if (!rawById.has(opt.id)) rawById.set(opt.id, opt);
  }

  const defaultIds = new Set(defaults.map(d => d.id));

  const mergedDefaults = defaults.map(def => {
    const existing = rawById.get(def.id);
    if (!existing) return def;
    return {
      ...def,
      ...existing,
      id: def.id,
    };
  });

  const seen = new Set<string>(mergedDefaults.map(d => d.id));

  const rest: VisibilityType[] = [];
  for (const opt of rawSafe) {
    if (defaultIds.has(opt.id)) continue;
    if (seen.has(opt.id)) continue;
    seen.add(opt.id);
    rest.push(opt);
  }

  return [...mergedDefaults, ...rest];
};

